import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Grid,
  CircularProgress,
} from '@material-ui/core';
import { getCandidatePassport } from './actions/getCandidates';

// Item 7 — admin read-only view of a candidate's passport/results. Fetches
// /admin/candidate/:id/passport and renders a defensive summary (the endpoint reuses
// the candidate-facing passport query, so shape matches { candidate, sponsor,
// technical, psychometric, passportRequest }).
const PASSPORT_FEEDBACK = { 0: 'Pending', 1: 'Issued', 2: 'Declined' };

const flagChips = (flags) => {
  if (!flags) return null;
  const items = [
    ['Self', flags.doneSelfAssessment],
    ['Technical', flags.doneTechAssessment],
    ['Psychometric', flags.donePsychAssessment],
    ['Passport enabled', flags.enablePassport],
  ];
  return items.map(([label, on]) => (
    <Chip
      key={label}
      label={label}
      size="small"
      style={{ marginRight: 6, marginBottom: 6 }}
      color={on ? 'primary' : 'default'}
    />
  ));
};

const scoreList = (arr) =>
  (arr || []).slice(0, 6).map((s, i) => (
    <Typography variant="body2" key={i}>
      {(s.topic && s.topic.name) || '—'}: {typeof s.score === 'number' ? s.score.toFixed(1) : s.score}
    </Typography>
  ));

const PassportModal = ({ open, candidate, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [passport, setPassport] = useState(null);

  useEffect(() => {
    if (!open || !candidate) return undefined;
    let mounted = true;
    setLoading(true);
    setPassport(null);
    getCandidatePassport(candidate.id).then((p) => {
      if (mounted) {
        setPassport(p);
        setLoading(false);
      }
    });
    return () => {
      mounted = false;
    };
  }, [open, candidate]);

  const p = passport || {};
  const cand = p.candidate || {};
  const sponsor = p.sponsor || {};
  const tech = p.technical || {};
  const psych = p.psychometric || {};
  const pr = p.passportRequest;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Passport — {candidate ? candidate.name : ''} {candidate ? `(${candidate.crn})` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <CircularProgress />}
        {!loading && !passport && (
          <Typography color="error">
            No passport data (candidate may not have an assignment/sponsor yet).
          </Typography>
        )}
        {!loading && passport && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Candidate</Typography>
              <Typography variant="body2">Discipline: {cand.discipline ? cand.discipline.name : '—'}</Typography>
              <Typography variant="body2">Role: {cand.role ? cand.role.name : '—'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Sponsor</Typography>
              <Typography variant="body2">
                {sponsor.firstName || ''} {sponsor.lastName || ''}
                {sponsor.company && sponsor.company.name ? ` — ${sponsor.company.name}` : ''}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Status</Typography>
              <div>{flagChips(cand.flags)}</div>
              <Typography variant="body2">
                Passport request: {pr ? PASSPORT_FEEDBACK[pr.feedback] || pr.feedback : 'None'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Technical</Typography>
              <Typography variant="body2">Safety: {tech.safety ? 'present' : '—'}</Typography>
              <Typography variant="body2">Completions: {tech.completions ? 'present' : '—'}</Typography>
              <Typography variant="body2">Role-specific: {tech.roleSpecific ? 'present' : '—'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Psychometric — top task responses</Typography>
              {scoreList(psych.types && psych.types.taskResponses)}
              <Typography variant="subtitle2" style={{ marginTop: 8 }}>Leadership</Typography>
              {scoreList(psych.types && psych.types.leadership)}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

PassportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  candidate: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

PassportModal.defaultProps = { candidate: null };

export default PassportModal;
